import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import FiscalList from '../components/fiscal/FiscalList';
import FiscalForm from '../components/fiscal/FiscalForm';
import { supabase } from '../lib/supabase';
import type { FiscalDocument } from '../types/fiscal';

const Fiscal: React.FC = () => {
  const [isNewDocument, setIsNewDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<FiscalDocument | null>(null);
  const [documents, setDocuments] = useState<FiscalDocument[]>([]);
  const location = useLocation();

  useEffect(() => {
    const state = location.state as { openNewDocument?: boolean };
    if (state?.openNewDocument) {
      setIsNewDocument(true);
      // Clear location state to prevent reopening form on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('fiscal_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading fiscal documents:', error);
    }
  };

  const handleDocumentSubmit = async (data: FiscalDocument) => {
    try {
      const { error } = await supabase
        .from('fiscal_documents')
        .insert([data]);

      if (error) throw error;

      await loadDocuments();
      setIsNewDocument(false);
    } catch (error) {
      console.error('Error saving fiscal document:', error);
    }
  };

  const handleSelectDocument = (document: FiscalDocument | null) => {
    if (document === null) {
      // This means we want to create a new document
      setIsNewDocument(true);
      setSelectedDocument(null);
    } else {
      setSelectedDocument(document);
      setIsNewDocument(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Documentos Fiscais
          </h1>
          <button
            onClick={() => handleSelectDocument(null)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Nova NF-e
          </button>
        </div>

        {isNewDocument ? (
          <FiscalForm 
            onSubmit={handleDocumentSubmit} 
            onCancel={() => setIsNewDocument(false)} 
          />
        ) : (
          <FiscalList 
            documents={documents} 
            onSelect={handleSelectDocument} 
          />
        )}
      </div>
    </div>
  );
};

export default Fiscal;